import type CustomerId from "../state/CustomerId";
import type FolderId from "../state/FolderId";
import type FormId from "../state/FormId";
import type Id from "../types/Id";
import UserService from "./UserService";
import type { PermissionType } from "../state/Permission";

type RequiredPermission =
  | ({
      type: PermissionType;
      resourceId: Id<string>;
    } & (
      | {
          type:
            | "MANAGE_ACCOUNT"
            | "MANAGE_CREDITS"
            | "MANAGE_DATA_SOURCES"
            | "MANAGE_FORMS"
            | "MANAGE_TASKS"
            | "MANAGE_REGISTRATIONS"
            | "MANAGE_USERS"
            | "MANAGE_WEBHOOKS"
            | "MANAGE_SSO"
            | "MANAGE_SENDING_DOMAIN"
            | "MANAGE_TEMPLATES"
            | "CREATE_FOLDER"
            | "CREATE_FORM"
            | "TRANSFER_OWNERSHIP"
            | "READ_REGISTRATION";
          resourceId: CustomerId;
        }
      | {
          type: "UPDATE_FOLDER" | "DELETE_FOLDER" | "ADD_FORM";
          resourceId: FolderId;
        }
      | {
          type:
            | "UPDATE_FORM"
            | "ARCHIVE_FORM"
            | "CREATE_TASK"
            | "READ_TASK"
            | "UPDATE_TASK"
            | "DELETE_TASK"
            | "READ_SUBMISSION"
            | "UPDATE_SUBMISSION"
            | "DELETE_SUBMISSION"
            | "DELETE_REGISTRATION";
          resourceId: FormId;
        }
    ))
  | {
      type: "ADMIN";
      // The 'ADMIN' grant is always on this object id (and is the same for DEV and PRD). Nobody knows exactly to which
      // document this object id belongs. It could be just a random object id, picked when adding this grant...
      resourceId: "60b07d865435a78b5dd79ac5";
    }
  | { type: "CREATE_CUSTOMERS" };

export const hasPermission = (requiredPermissions: RequiredPermission[][]) => {
  const permissionsQuery = UserService.getPermissions();
  return requiredPermissions.some((rp) =>
    rp.every((rp) =>
      "resourceId" in rp
        ? (permissionsQuery.data?.[rp.type]?.includes(rp.resourceId.toString()) ?? false)
        : permissionsQuery.data?.[rp.type] !== undefined,
    ),
  );
};

const PermissionService = {
  isAdmin: () => hasPermission([[{ type: "ADMIN", resourceId: "60b07d865435a78b5dd79ac5" }]]),
  isAccountOwner: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_ACCOUNT", resourceId: customerId }]]),
  canCreateFolder: (customerId: CustomerId) =>
    hasPermission([
      [{ type: "MANAGE_FORMS", resourceId: customerId }],
      [{ type: "CREATE_FOLDER", resourceId: customerId }],
    ]),
  canEditFolder: (customerId: CustomerId, folderId: FolderId) =>
    hasPermission([
      [{ type: "MANAGE_FORMS", resourceId: customerId }],
      [{ type: "UPDATE_FOLDER", resourceId: folderId }],
    ]),
  canDeleteFolder: (customerId: CustomerId, folderId: FolderId) =>
    hasPermission([
      [{ type: "MANAGE_FORMS", resourceId: customerId }],
      [{ type: "DELETE_FOLDER", resourceId: folderId }],
    ]),
  canCreateForm: (customerId: CustomerId, folderId: FolderId) =>
    hasPermission([
      [{ type: "MANAGE_FORMS", resourceId: customerId }],
      [
        { type: "CREATE_FORM", resourceId: customerId },
        { type: "ADD_FORM", resourceId: folderId },
      ],
    ]),
  canEditForm: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_FORMS", resourceId: customerId }], [{ type: "UPDATE_FORM", resourceId: formId }]]),
  canDeleteForm: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_FORMS", resourceId: customerId }], [{ type: "ARCHIVE_FORM", resourceId: formId }]]),
  canMoveForm: (customerId: CustomerId, folderId: FolderId, formId: FormId) =>
    hasPermission([
      [{ type: "MANAGE_FORMS", resourceId: customerId }],
      [
        { type: "UPDATE_FOLDER", resourceId: folderId },
        { type: "UPDATE_FORM", resourceId: formId },
      ],
    ]),
  canCopyForm: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_FORMS", resourceId: customerId }]]),
  canReorderForm: (customerId: CustomerId, folderId: FolderId, formId: FormId) =>
    hasPermission([
      [{ type: "MANAGE_FORMS", resourceId: customerId }],
      [
        { type: "UPDATE_FOLDER", resourceId: folderId },
        { type: "UPDATE_FORM", resourceId: formId },
      ],
    ]),
  canShareForm: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_FORMS", resourceId: customerId }], [{ type: "UPDATE_FORM", resourceId: formId }]]),
  canCreateFormTasks: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_TASKS", resourceId: customerId }], [{ type: "CREATE_TASK", resourceId: formId }]]),
  canReadFormTasks: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_TASKS", resourceId: customerId }], [{ type: "READ_TASK", resourceId: formId }]]),
  canUpdateFormTasks: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_TASKS", resourceId: customerId }], [{ type: "UPDATE_TASK", resourceId: formId }]]),
  canDeleteFormTasks: (customerId: CustomerId, formId: FormId) =>
    hasPermission([[{ type: "MANAGE_TASKS", resourceId: customerId }], [{ type: "DELETE_TASK", resourceId: formId }]]),
  canReadFormRegistrations: (customerId: CustomerId, formId: FormId) =>
    hasPermission([
      [{ type: "MANAGE_REGISTRATIONS", resourceId: customerId }],
      [{ type: "READ_REGISTRATION", resourceId: customerId }],
      [{ type: "READ_SUBMISSION", resourceId: formId }],
    ]),
  canManageDataSources: (customerId: CustomerId) =>
    hasPermission([[{ type: "MANAGE_DATA_SOURCES", resourceId: customerId }]]),
  canManagePermissions: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_USERS", resourceId: customerId }]]),
  canManageCompanyInformation: (customerId: CustomerId) =>
    hasPermission([[{ type: "MANAGE_ACCOUNT", resourceId: customerId }]]),
  canManageBilling: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_CREDITS", resourceId: customerId }]]),
  canManageApiKeys: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_USERS", resourceId: customerId }]]),
  canManageWebhooks: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_WEBHOOKS", resourceId: customerId }]]),
  canManageEmailConfiguration: (customerId: CustomerId) =>
    hasPermission([[{ type: "MANAGE_SENDING_DOMAIN", resourceId: customerId }]]),
  canManageServiceAccounts: (customerId: CustomerId) =>
    hasPermission([[{ type: "MANAGE_FORMS", resourceId: customerId }]]),
  canManageSSO: (customerId: CustomerId) => hasPermission([[{ type: "MANAGE_SSO", resourceId: customerId }]]),
  canManageRegistrations: (customerId: CustomerId) =>
    hasPermission([[{ type: "MANAGE_REGISTRATIONS", resourceId: customerId }]]),
  canResendSubmission: (customerId: CustomerId, formId: FormId) =>
    hasPermission([
      [{ type: "MANAGE_REGISTRATIONS", resourceId: customerId }],
      [{ type: "UPDATE_SUBMISSION", resourceId: formId }],
    ]),
  canDeleteSubmission: (customerId: CustomerId, formId: FormId) =>
    hasPermission([
      [{ type: "MANAGE_REGISTRATIONS", resourceId: customerId }],
      [{ type: "DELETE_SUBMISSION", resourceId: formId }],
      [{ type: "DELETE_REGISTRATION", resourceId: formId }],
    ]),
  canTransferOwnership: (customerId: CustomerId) =>
    hasPermission([[{ type: "TRANSFER_OWNERSHIP", resourceId: customerId }]]),
  canCreateCustomers: (customerId: CustomerId) =>
    hasPermission([[{ type: "CREATE_CUSTOMERS" }, { type: "MANAGE_USERS", resourceId: customerId }]]),
};

export default PermissionService;
